<template>
  <pos-layout>
    <div>
      <b-container class="text-left pt-4">
        <h3>Printer ESC/POS Test Suite</h3>
        <p class="text-muted">
          Test and customize printer commands, font sizes, margins, and formatting options
        </p>
        
        <!-- Printer Configuration Info -->
        <b-card
          title="Printer Configuration"
          class="mb-4"
        >
          <div class="row">
            <div class="col-md-6">
              <div class="small text-muted">
                <div><strong>Printer Name:</strong> {{ (terminalInfo.printer && terminalInfo.printer.name) || 'Not configured' }}</div>
                <div><strong>Printer IP:</strong> {{ (terminalInfo.printer && terminalInfo.printer.local_ip) || 'Not configured' }}</div>
                <div><strong>Printer Server IP:</strong> {{ terminalInfo.printer_server_ip || 'Not configured' }}</div>
                <div><strong>Is 58mm:</strong> {{ (terminalInfo.printer && terminalInfo.printer.is_58mm) ? 'Yes' : 'No' }}</div>
              </div>
            </div>
            <div class="col-md-6">
              <div><strong>QZ Tray Status:</strong> <span :class="qzTrayStatus.class">{{ qzTrayStatus.text }}</span></div>
              <b-button
                variant="warning"
                size="sm"
                @click="checkQZTrayStatus"
                class="mt-2"
              >
                Check QZ Tray Status
              </b-button>
            </div>
          </div>
        </b-card>

        <!-- Printer Width Configuration -->
        <b-card
          title="Printer Width Configuration"
          class="mb-4"
        >
          <b-form-group
            label="Select Printer Width:"
            label-for="printer-width"
          >
            <b-form-select
              id="printer-width"
              v-model="printerWidth"
              :options="availableWidths"
              :disabled="isPrinting"
            />
          </b-form-group>
          <div class="text-muted">
            <small>
              <strong>Current Width:</strong> {{ printerWidth }} 
              <span v-if="printerWidth === '58mm'">- Standard receipt printer</span>
              <span v-else-if="printerWidth === '80mm'">- Wide receipt printer</span>
              <span v-else-if="printerWidth === '110mm'">- A4 width printer</span>
            </small>
          </div>
        </b-card>

        <!-- Comprehensive Printer Test -->
        <b-card
          title="Complete Printer Feature Test"
          class="mb-4"
        >
          <p>
            Print a comprehensive test receipt that demonstrates all printer capabilities including:
          </p>
          <ul>
            <li><strong>Font Sizes:</strong> Normal, Double, Large</li>
            <li><strong>Text Alignments:</strong> Left, Center, Right</li>
            <li><strong>Text Formatting:</strong> Bold, Underline, Inverse</li>
            <li><strong>Special Characters:</strong> Symbols, Currency, Copyright</li>
            <li><strong>Barcodes:</strong> EAN13, CODE39</li>
            <li><strong>QR Codes:</strong> QR code generation</li>
            <li><strong>Paper Control:</strong> Line feeds, cuts</li>
          </ul>
          
          <b-button
            variant="success"
            size="lg"
            @click="printCompleteFeatureTest"
            :disabled="isPrinting"
            class="w-100 mb-2"
          >
            {{ isPrinting ? 'Printing Complete Test...' : 'Print Complete Feature Test' }}
          </b-button>
          
          <div class="row">
            <div class="col-6">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="printFontTest"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Font Test
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="printAlignmentTest"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Alignment Test
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="printBarcodeTest"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Barcode Test
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="printHTMLTest"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                HTML Print Test
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="printHTMLWithRealQR"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                HTML + Real QR Code
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="printMixedTest"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Mixed ESC/POS + HTML Test
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="info"
                size="sm"
                @click="printBeautifulReceipt"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Beautiful Receipt
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="warning"
                size="sm"
                @click="printModernReceipt"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Modern Receipt
              </b-button>
            </div>
            <div class="col-6">
              <b-button
                variant="success"
                size="sm"
                @click="printKitchenOrderTest"
                :disabled="isPrinting"
                class="w-100 mb-1"
              >
                Kitchen Order Test
              </b-button>
            </div>
          </div>
        </b-card>

        <!-- Custom Test -->
        <b-card
          title="Custom ESC/POS Test"
          class="mb-4"
        >
          <b-form-group label="Custom ESC/POS Commands">
            <b-textarea
              v-model="customCommands"
              placeholder="Enter custom ESC/POS commands here..."
              rows="4"
            />
            <small class="text-muted">
              Example: \x1B\x45\x01 (Bold ON), \x1B\x45\x00 (Bold OFF), \x1B\x61\x01 (Center)
            </small>
          </b-form-group>
          <b-button
            variant="primary"
            @click="testCustomCommands"
            :disabled="isPrinting || !customCommands.trim()"
          >
            {{ isPrinting ? 'Printing...' : 'Print Custom Commands' }}
          </b-button>
        </b-card>


        <!-- Test Results -->
        <b-card
          title="Test Results"
          v-if="testResults.length > 0"
        >
          <div
            v-for="(result, index) in testResults"
            :key="index"
            class="mb-2"
          >
            <div :class="result.success ? 'text-success' : 'text-danger'">
              <strong>{{ result.timestamp }}:</strong> {{ result.message }}
            </div>
          </div>
        </b-card>
      </b-container>
    </div>
  </pos-layout>
</template>

<script>
import PosLayout from '../layouts/PosLayout';
import * as qz from 'qz-tray';
import { sendToPrinter } from '../helpers/print/printer';

export default {
  name: 'PrinterTest',
  components: {
    PosLayout
  },
  data() {
    return {
      isPrinting: false,
      qzTrayConnected: false,
      customCommands: '',
      testResults: [],
      printerWidth: '80mm', // Default to 80mm
      availableWidths: [
        { value: '58mm', text: '58mm (Standard Receipt)' },
        { value: '80mm', text: '80mm (Wide Receipt)' },
        { value: '110mm', text: '110mm (A4 Width)' }
      ]
      };
  },
  mounted() {
    this.checkQZTrayStatusOnLoad();
  },
  computed: {
    terminalInfo() {
      return this.$store.getters["config/terminal"];
    },
    qzTrayStatus() {
      if (this.qzTrayConnected) {
        return { text: 'Connected', class: 'text-success' };
      } else {
        return { text: 'Disconnected', class: 'text-danger' };
      }
    },
    printerWidthPx() {
      switch (this.printerWidth) {
        case '58mm':
          return '200px'; // 58mm ≈ 200px at 96 DPI
        case '80mm':
          return '280px'; // 80mm ≈ 280px at 96 DPI
        case '110mm':
          return '380px'; // 110mm ≈ 380px at 96 DPI
        default:
          return '280px';
      }
    },
    printerWidthMm() {
      return this.printerWidth.replace('mm', '');
    }
  },
  methods: {
    getWidthSeparator() {
      // Create a separator line that fits the printer width
      const separatorLength = this.printerWidthMm;
      let separator = '';
      for (let i = 0; i < separatorLength; i++) {
        separator += '=';
      }
      return separator;
    },
    
    async checkQZTrayStatusOnLoad() {
      try {
        this.qzTrayConnected = qz.websocket.isActive();
      } catch (error) {
        console.error('Error checking QZ Tray status on load:', error);
        this.qzTrayConnected = false;
      }
    },

    async checkQZTrayStatus() {
      try {
        const terminalInfo = this.$store.getters["config/terminal"];
        const connectOptions = terminalInfo.printer_server_ip ? { host: terminalInfo.printer_server_ip } : {};
        
        if (!qz.websocket.isActive()) {
          await qz.websocket.connect(connectOptions);
          this.qzTrayConnected = true;
          this.addTestResult(true, 'QZ Tray connected successfully');
        } else {
          this.qzTrayConnected = true;
          this.addTestResult(true, 'QZ Tray is already connected');
        }
      } catch (error) {
        console.error('QZ Tray connection error:', error);
        this.qzTrayConnected = false;
        this.addTestResult(false, 'Failed to connect to QZ Tray: ' + error.message);
      }
    },

    addTestResult(success, message) {
      this.testResults.unshift({
        timestamp: new Date().toLocaleTimeString(),
        success: success,
        message: message
      });
      // Keep only last 10 results
      if (this.testResults.length > 10) {
        this.testResults = this.testResults.slice(0, 10);
      }
    },

    async ensureQZTrayConnection() {
      if (!qz.websocket.isActive()) {
        const terminalInfo = this.$store.getters["config/terminal"];
        const connectOptions = terminalInfo.printer_server_ip ? { host: terminalInfo.printer_server_ip } : {};
        await qz.websocket.connect(connectOptions);
        this.qzTrayConnected = true;
      }
    },


    async testCustomCommands() {
      this.isPrinting = true;
      try {
        await this.ensureQZTrayConnection();
        
        // Convert string commands to actual escape sequences
        let commands = this.customCommands
          .replace(/\\x([0-9A-Fa-f]{2})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
          .replace(/\\n/g, '\n')
          .replace(/\\r/g, '\r')
          .replace(/\\t/g, '\t');
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Custom commands printed successfully');
        
      } catch (error) {
        console.error('Custom commands test error:', error);
        this.addTestResult(false, `Custom commands failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printCompleteFeatureTest() {
      this.isPrinting = true;
      try {
        await this.ensureQZTrayConnection();
        
        let commands = '\x1B\x40'; // Initialize printer
        
        // Header with store info
        commands += '\x1B\x61\x01'; // Center align
        commands += '=== PRINTER FEATURE TEST ===\n';
        commands += 'Complete ESC/POS Capabilities\n';
        commands += '==============================\n';
        commands += '\x1B\x61\x00'; // Left align
        
        // Font Size Tests
        commands += '\n--- FONT SIZE TESTS ---\n';
        commands += 'Small Size: Hello World\n';
        commands += '\x1B\x21\x10'; // Small font
        commands += 'Small Size: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += 'Normal Size: Hello World\n';
        commands += '\x1B\x21\x20'; // Double height
        commands += 'Double Height: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x40'; // Double width
        commands += 'Double Width: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x60'; // Double width and height
        commands += 'Double Size: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x30'; // Large font
        commands += 'Large Size: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x0A'; // Line feed
        
        // Alignment Tests
        commands += '\n--- ALIGNMENT TESTS ---\n';
        commands += '\x1B\x61\x00'; // Left align
        commands += 'Left Aligned Text\n';
        commands += '\x1B\x61\x01'; // Center align
        commands += 'Center Aligned Text\n';
        commands += '\x1B\x61\x02'; // Right align
        commands += 'Right Aligned Text\n';
        commands += '\x1B\x61\x00'; // Reset to left
        commands += '\x0A'; // Line feed
        
        // Text Formatting Tests
        commands += '\n--- TEXT FORMATTING ---\n';
        commands += 'Normal Text\n';
        commands += '\x1B\x45\x01'; // Bold on
        commands += 'Bold Text\n';
        commands += '\x1B\x45\x00'; // Bold off
        commands += '\x1B\x2D\x01'; // Underline on
        commands += 'Underlined Text\n';
        commands += '\x1B\x2D\x00'; // Underline off
        commands += '\x1D\x42\x01'; // Inverse on
        commands += 'Inverse Text\n';
        commands += '\x1D\x42\x00'; // Inverse off
        commands += '\x0A'; // Line feed
        
        // Font Sizes with Inverse Text
        commands += '\n--- FONT SIZES WITH INVERSE ---\n';
        commands += '\x1D\x42\x01'; // Inverse on
        commands += '\x1B\x21\x10'; // Small font
        commands += 'Small Inverse\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += 'Normal Inverse\n';
        commands += '\x1B\x21\x20'; // Double height
        commands += 'Double Height Inverse\n';
        commands += '\x1B\x21\x40'; // Double width
        commands += 'Double Width Inverse\n';
        commands += '\x1B\x21\x60'; // Double size
        commands += 'Double Size Inverse\n';
        commands += '\x1B\x21\x30'; // Large font
        commands += 'Large Inverse\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1D\x42\x00'; // Inverse off
        commands += '\x0A'; // Line feed
        
        // Special Characters
        commands += '\n--- SPECIAL CHARACTERS ---\n';
        commands += 'Currency: $ € £ ¥ ₹ ₽ ₩ ₪ ₫ ₭ ₮ ₯ ₰ ₱ ₲ ₳ ₴ ₵ ₶ ₷ ₸ ₹ ₺ ₻ ₼ ₽ ₾ ₿\n';
        commands += 'Symbols: © ® ™ § ¶ † ‡ • … ‰ ′ ″ ‴ ‵ ‶ ‷ ‸ ‹ › ※ ‼ ‽ ‾ ‿ ⁀ ⁁ ⁂ ⁃ ⁄ ⁅ ⁆ ⁇ ⁈ ⁉ ⁊ ⁋ ⁌ ⁍ ⁎ ⁏ ⁐ ⁑ ⁒ ⁓ ⁔ ⁕ ⁖ ⁗ ⁘ ⁙ ⁚ ⁛ ⁜ ⁝ ⁞\n';
        commands += 'Math: ± × ÷ ∞ ≤ ≥ ≠ ≈ ≡ ∑ ∏ ∫ ∂ ∇ ∆ ∝ ∠ ∟ ⊥ ∥ ∦ ∝ ∟ ∠ ∡ ∢ ∤ ∥ ∦ ∝ ∟ ∠ ∡ ∢ ∤ ∥ ∦\n';
        commands += '\x0A'; // Line feed
        
        // Barcode Tests
        commands += '\n--- BARCODE TESTS ---\n';
        commands += 'EAN13 Barcode:\n';
        commands += '\x1D\x6B\x02'; // EAN13 barcode
        commands += '123456789012';
        commands += '\x00'; // End barcode
        commands += '\x0A\x0A'; // Double line feed
        
        commands += 'CODE39 Barcode:\n';
        commands += '\x1D\x6B\x04'; // CODE39 barcode
        commands += 'TEST123';
        commands += '\x00'; // End barcode
        commands += '\x0A\x0A'; // Double line feed
        
        // QR Code Test (Proper ESC/POS sequence)
        commands += '\n--- QR CODE TEST ---\n';
        
        // QR Code 1: "Hello World" (Model 2, Size 3, 15% error correction)
        commands += 'QR Code 1: Hello World\n';
        const qrData1 = 'Hello World';
        const storeLen1 = qrData1.length + 3;
        const storePL1 = storeLen1 % 256;
        const storePH1 = Math.floor(storeLen1 / 256);
        
        commands += '\x1D\x28\x6B\x04\x00\x31\x41\x32\x00'; // Model 2
        commands += '\x1D\x28\x6B\x03\x00\x31\x43\x03'; // Size 3
        commands += '\x1D\x28\x6B\x03\x00\x31\x45\x31'; // 15% error correction
        commands += '\x1D\x28\x6B' + String.fromCharCode(storePL1, storePH1) + '\x31\x50\x30'; // Store data
        commands += qrData1;
        commands += '\x1D\x28\x6B\x03\x00\x31\x51\x30'; // Print
        commands += '\x0A\x0A'; // Double line feed
        
        // QR Code 2: "Test123" (Model 1, Size 4, 7% error correction)
        commands += 'QR Code 2: Test123\n';
        const qrData2 = 'Test123';
        const storeLen2 = qrData2.length + 3;
        const storePL2 = storeLen2 % 256;
        const storePH2 = Math.floor(storeLen2 / 256);
        
        commands += '\x1D\x28\x6B\x04\x00\x31\x41\x31\x00'; // Model 1
        commands += '\x1D\x28\x6B\x03\x00\x31\x43\x04'; // Size 4
        commands += '\x1D\x28\x6B\x03\x00\x31\x45\x30'; // 7% error correction
        commands += '\x1D\x28\x6B' + String.fromCharCode(storePL2, storePH2) + '\x31\x50\x30'; // Store data
        commands += qrData2;
        commands += '\x1D\x28\x6B\x03\x00\x31\x51\x30'; // Print
        commands += '\x0A\x0A'; // Double line feed
        
        // QR Code 3: "Hi" (Model 1, Size 1, 7% error correction)
        commands += 'QR Code 3: Hi (Small)\n';
        const qrData3 = 'Hi';
        const storeLen3 = qrData3.length + 3;
        const storePL3 = storeLen3 % 256;
        const storePH3 = Math.floor(storeLen3 / 256);
        
        commands += '\x1D\x28\x6B\x04\x00\x31\x41\x31\x00'; // Model 1
        commands += '\x1D\x28\x6B\x03\x00\x31\x43\x01'; // Size 1
        commands += '\x1D\x28\x6B\x03\x00\x31\x45\x30'; // 7% error correction
        commands += '\x1D\x28\x6B' + String.fromCharCode(storePL3, storePH3) + '\x31\x50\x30'; // Store data
        commands += qrData3;
        commands += '\x1D\x28\x6B\x03\x00\x31\x51\x30'; // Print
        commands += '\x0A\x0A'; // Double line feed
        
        // Simple Bitmap Test
        commands += '\n--- SIMPLE BITMAP TEST ---\n';
        commands += 'Testing basic bitmap support:\n';
        commands += '\x1D\x76\x30\x00'; // GS v 0 (bitmap mode)
        commands += '\x00\x00\x08\x00'; // 8x8 bitmap (8 pixels wide, 8 pixels high)
        // Simple 8x8 test pattern (checkerboard)
        commands += '\xFF\x00\xFF\x00\xFF\x00\xFF\x00'; // 8 bytes of bitmap data
        commands += '\x0A\x0A'; // Double line feed
        
        // QR Code Debugging
        commands += '\n--- QR CODE DEBUGGING ---\n';
        commands += 'QR Code Debug Info:\n';
        commands += 'Model 2: 0x1D 0x28 0x6B 0x04 0x00 0x31 0x41 0x32 0x00\n';
        commands += 'Size 3:  0x1D 0x28 0x6B 0x03 0x00 0x31 0x43 0x03\n';
        commands += 'Error 15%: 0x1D 0x28 0x6B 0x03 0x00 0x31 0x45 0x31\n';
        commands += 'Store:   0x1D 0x28 0x6B [pL] [pH] 0x31 0x50 0x30\n';
        commands += 'Print:   0x1D 0x28 0x6B 0x03 0x00 0x31 0x51 0x30\n';
        commands += '\x0A'; // Line feed
        
        // Note about QR Code support
        commands += 'Note: If QR codes don\'t appear above,\n';
        commands += 'your printer may not support QR codes.\n';
        commands += 'Try updating printer firmware or\n';
        commands += 'check printer documentation.\n';
        commands += '\x0A'; // Line feed
        
        // Paper Control Tests
        commands += '\n--- PAPER CONTROL ---\n';
        commands += 'Line Feed Test:\n';
        commands += 'Line 1\n';
        commands += 'Line 2\n';
        commands += 'Line 3\n';
        commands += '\x0A\x0A'; // Double line feed
        
        // Table/Receipt Formatting
        commands += '\n--- TABLE FORMATTING ---\n';
        commands += 'Item Name                    Price\n';
        commands += '--------------------------------\n';
        commands += 'Test Item 1                  $10.00\n';
        commands += 'Test Item 2                  $15.50\n';
        commands += 'Test Item 3                  $25.99\n';
        commands += '--------------------------------\n';
        commands += 'Subtotal                     $51.49\n';
        commands += 'Tax (8.25%)                  $4.25\n';
        commands += 'Total                        $55.74\n';
        commands += '\x0A'; // Line feed
        
        // Additional Font Size Tests
        commands += '\n--- ADDITIONAL FONT TESTS ---\n';
        commands += '\x1B\x21\x08'; // Very small font
        commands += 'Very Small: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x18'; // Small + Bold
        commands += 'Small Bold: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x28'; // Double height + Bold
        commands += 'Double Height Bold: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x48'; // Double width + Bold
        commands += 'Double Width Bold: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x0A'; // Line feed
        
        // Footer
        commands += '\n--- TEST COMPLETED ---\n';
        commands += '\x1B\x61\x01'; // Center align
        commands += 'Printer Feature Test\n';
        commands += 'Completed Successfully\n';
        commands += new Date().toLocaleString() + '\n';
        commands += '\x1B\x61\x00'; // Left align
        commands += '\x0A\x0A'; // Double line feed
        
        // Paper cut
        commands += '\x1D\x56\x00'; // Full cut
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Complete feature test printed successfully');
        
      } catch (error) {
        console.error('Complete feature test error:', error);
        this.addTestResult(false, `Complete feature test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printFontTest() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting font test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        let commands = '';
        commands += '\x1B\x40'; // Initialize printer
        commands += '\x1B\x61\x01'; // Center alignment
        commands += `\n=== FONT SIZE TEST (${this.printerWidth}) ===\n`;
        commands += '\x1B\x61\x00'; // Left alignment
        
        // Width-specific content
        if (this.printerWidth === '58mm') {
          commands += '\n58mm Standard Receipt Test\n';
          commands += 'This is a standard receipt width\n';
          commands += 'Perfect for small thermal printers\n';
        } else if (this.printerWidth === '80mm') {
          commands += '\n80mm Wide Receipt Test\n';
          commands += 'This is a wide receipt width\n';
          commands += 'Perfect for larger thermal printers\n';
        } else if (this.printerWidth === '110mm') {
          commands += '\n110mm A4 Width Test\n';
          commands += 'This is an A4 width receipt\n';
          commands += 'Perfect for wide format printers\n';
        }
        
        // Font Size Tests
        commands += '\n--- Font Sizes ---\n';
        commands += '\x1B\x21\x08'; // Very Small
        commands += 'Very Small: Hello World\n';
        commands += '\x1B\x21\x10'; // Small
        commands += 'Small: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal
        commands += 'Normal: Hello World\n';
        commands += '\x1B\x21\x20'; // Double Height
        commands += 'Double Height: Hello World\n';
        commands += '\x1B\x21\x40'; // Double Width
        commands += 'Double Width: Hello World\n';
        commands += '\x1B\x21\x60'; // Double Size
        commands += 'Double Size: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        
        // Font Sizes with Bold
        commands += '\n--- Font Sizes with Bold ---\n';
        commands += '\x1B\x21\x18'; // Small + Bold
        commands += 'Small Bold: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x28'; // Double height + Bold
        commands += 'Double Height Bold: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x1B\x21\x48'; // Double width + Bold
        commands += 'Double Width Bold: Hello World\n';
        commands += '\x1B\x21\x00'; // Normal font
        commands += '\x0A'; // Line feed
        
        // Width-specific separator
        commands += '\n';
        commands += this.getWidthSeparator();
        commands += '\n';
        
        commands += '\n--- Font Test Completed ---\n';
        commands += '\x0A\x0A'; // Double line feed
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Font test printed successfully');
        
      } catch (error) {
        console.error('Font test failed:', error);
        this.addTestResult(false, `Font test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printAlignmentTest() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting alignment test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        let commands = '';
        commands += '\x1B\x40'; // Initialize printer
        commands += `\n=== ALIGNMENT TEST (${this.printerWidth}) ===\n`;
        
        // Width-specific content
        if (this.printerWidth === '58mm') {
          commands += '\n58mm Standard Receipt Test\n';
          commands += 'This is a standard receipt width\n';
          commands += 'Perfect for small thermal printers\n';
        } else if (this.printerWidth === '80mm') {
          commands += '\n80mm Wide Receipt Test\n';
          commands += 'This is a wide receipt width\n';
          commands += 'Perfect for larger thermal printers\n';
        } else if (this.printerWidth === '110mm') {
          commands += '\n110mm A4 Width Test\n';
          commands += 'This is an A4 width receipt\n';
          commands += 'Perfect for wide format printers\n';
        }
        
        // Alignment Tests
        commands += '\n--- Text Alignment ---\n';
        commands += '\x1B\x61\x00'; // Left align
        commands += 'Left Aligned Text\n';
        commands += '\x1B\x61\x01'; // Center align
        commands += 'Center Aligned Text\n';
        commands += '\x1B\x61\x02'; // Right align
        commands += 'Right Aligned Text\n';
        commands += '\x1B\x61\x00'; // Left align
        
        // Width-specific separator
        commands += '\n';
        commands += this.getWidthSeparator();
        commands += '\n';
        
        commands += '\n--- Alignment Test Completed ---\n';
        commands += '\x0A\x0A'; // Double line feed
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Alignment test printed successfully');
        
      } catch (error) {
        console.error('Alignment test failed:', error);
        this.addTestResult(false, `Alignment test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printBarcodeTest() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting barcode test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        let commands = '';
        commands += '\x1B\x40'; // Initialize printer
        commands += '\x1B\x61\x01'; // Center alignment
        commands += `\n=== BARCODE TEST (${this.printerWidth}) ===\n`;
        commands += '\x1B\x61\x00'; // Left alignment
        
        // Width-specific content
        if (this.printerWidth === '58mm') {
          commands += '\n58mm Standard Receipt Test\n';
          commands += 'This is a standard receipt width\n';
          commands += 'Perfect for small thermal printers\n';
        } else if (this.printerWidth === '80mm') {
          commands += '\n80mm Wide Receipt Test\n';
          commands += 'This is a wide receipt width\n';
          commands += 'Perfect for larger thermal printers\n';
        } else if (this.printerWidth === '110mm') {
          commands += '\n110mm A4 Width Test\n';
          commands += 'This is an A4 width receipt\n';
          commands += 'Perfect for wide format printers\n';
        }
        
        // Barcode Tests
        commands += '\n--- Barcodes ---\n';
        commands += 'EAN13 Barcode:\n';
        commands += '\x1D\x6B\x02'; // EAN13
        commands += '123456789012'; // 12 digits
        commands += '\x0A'; // Line feed
        
        commands += 'CODE39 Barcode:\n';
        commands += '\x1D\x6B\x04'; // CODE39
        commands += 'HELLO123'; // CODE39 data
        commands += '\x0A'; // Line feed
        
        // Width-specific separator
        commands += '\n';
        commands += this.getWidthSeparator();
        commands += '\n';
        
        commands += '\n--- Barcode Test Completed ---\n';
        commands += '\x0A\x0A'; // Double line feed
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Barcode test printed successfully');
        
      } catch (error) {
        console.error('Barcode test failed:', error);
        this.addTestResult(false, `Barcode test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printKitchenOrderTest() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting kitchen order test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        // Sample data for testing
        const receiptNumber = '12345';
        const customerName = 'John Doe';
        const now = new Date();
        const orderDate = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const orderTimeOnly = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        const orderTime = `${orderDate} - ${orderTimeOnly}`;
        const tableName = 'Table 5';
        const items = [
          { qty: 2, name: 'Cheeseburger', modifiers: ['No Onions', 'Extra Cheese', 'Well Done'] },
          { qty: 1, name: 'Caesar Salad', modifiers: ['Dressing on Side'] },
          { qty: 3, name: 'French Fries', modifiers: [] },
          { qty: 1, name: 'Grilled Salmon', modifiers: ['No Butter', 'Extra Lemon'] }
        ];
        const totalItems = items.reduce((sum, item) => sum + item.qty, 0);
        
        // Determine separator length based on printer width
        let separator = '';
        if (this.printerWidth === '58mm') {
          separator = '-'.repeat(30);
        } else if (this.printerWidth === '80mm') {
          separator = '-'.repeat(42);
        } else {
          separator = '-'.repeat(56);
        }
        
        let commands = '';
        commands += '\x1B\x40'; // Initialize printer
        
        // Bold and Double Height for NEW ORDER
        commands += '\x1B\x61\x01'; // Center alignment
        commands += '\n' + separator + '\n';
        commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width
        commands += '!!! NEW ORDER !!!\n';
        commands += '\x1B\x21\x00'; // Reset text formatting
        commands += separator + '\n\n';
        
        // Left alignment for details
        commands += '\x1B\x61\x00'; // Left alignment
        
        // Receipt Number
        commands += '\x1B\x21\x00'; // Normal size for label
        commands += 'Receipt #\n';
        commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width (for value)
        commands += `${receiptNumber}\n`;
        commands += '\x1B\x21\x00'; // Reset
        commands += '\n';
        
        // Customer Name
        commands += '\x1B\x21\x00'; // Normal size for label
        commands += 'Customer Name\n';
        commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width (for value)
        commands += `${customerName}\n`;
        commands += '\x1B\x21\x00'; // Reset
        commands += '\n';
        
        // Order Time
        commands += '\x1B\x21\x00'; // Normal size for label
        commands += 'Order Time\n';
        commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width (for value)
        commands += `${orderTime}\n`;
        commands += '\x1B\x21\x00'; // Reset
        commands += '\n';
        
        // Table (only print if tableName exists)
        if (tableName) {
          commands += '\x1B\x21\x00'; // Normal size for label
          commands += 'Table\n';
          commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width (for value)
          commands += `${tableName}\n`;
          commands += '\x1B\x21\x00'; // Reset
          commands += '\n';
        }
        
        // Total Items
        commands += '\x1B\x21\x00'; // Normal size for label
        commands += 'Total Items\n';
        commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width (for value)
        commands += `${totalItems} items\n`;
        commands += '\x1B\x21\x00'; // Reset
        commands += '\n';
        
        // Items section
        items.forEach((item, index) => {
          // Separator before item
          commands += '\x1B\x21\x00'; // Normal size for separator
          commands += separator + '\n';
          
          // Item with quantity - Bold + Double size
          commands += '\x1B\x21\x38'; // Bold + Double Height + Double Width
          commands += `${item.qty} x ${item.name}\n`;
          
          // Modifiers if any - Double size only (no bold)
          if (item.modifiers && item.modifiers.length > 0) {
            commands += '\x1B\x21\x20'; // Double Height only
            item.modifiers.forEach(modifier => {
              commands += `* ${modifier}\n`; // Asterisk as bullet point
            });
          }
          
          // Add spacing between items (except after last item)
          if (index < items.length - 1) {
            commands += '\n';
          }
        });
        
        // Final separator after all items
        commands += '\x1B\x21\x00'; // Normal size for separator
        commands += separator + '\n';
        
        // Final spacing and cut
        commands += '\x1B\x21\x00'; // Reset to normal
        commands += '\n\n';
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Kitchen order test printed successfully');
        
      } catch (error) {
        console.error('Kitchen order test failed:', error);
        this.addTestResult(false, `Kitchen order test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },


    async printRasterQRCode() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting raster QR code test...');

      try {
        await this.ensureQZTrayConnection();
        
        let commands = '';
        commands += '\x1B\x40'; // Initialize printer
        commands += '\x1B\x61\x01'; // Center alignment
        commands += '\n=== RASTER QR CODE TEST ===\n';
        commands += '\x1B\x61\x00'; // Left alignment
        
        // Create a simple QR code pattern as a raster image
        commands += '\n--- Simple QR Code Pattern ---\n';
        commands += 'Creating QR-like pattern as raster image:\n';
        
        // Create a 32x32 QR-like pattern
        const size = 32;
        const bytesPerLine = Math.ceil(size / 8);
        
        const xL = size % 256;
        const xH = Math.floor(size / 256);
        const yL = size % 256;
        const yH = Math.floor(size / 256);
        
        commands += '\x1D\x76\x30\x00'; // GS v 0 (raster mode)
        commands += String.fromCharCode(xL, xH, yL, yH); // Dimensions
        
        // Create a simple QR-like pattern (3 corner squares + center)
        for (let y = 0; y < size; y++) {
          for (let x = 0; x < bytesPerLine; x++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
              const pixelX = x * 8 + bit;
              if (pixelX < size) {
                // Create QR-like pattern
                if (this.isQRPatternPixel(pixelX, y, size)) {
                  byte |= (1 << (7 - bit));
                }
              }
            }
            commands += String.fromCharCode(byte);
          }
        }
        commands += '\x0A\x0A'; // Double line feed
        
        // Create a larger 48x48 QR pattern
        commands += '\n--- Larger QR Pattern (48x48) ---\n';
        const size2 = 48;
        const bytesPerLine2 = Math.ceil(size2 / 8);
        
        const xL2 = size2 % 256;
        const xH2 = Math.floor(size2 / 256);
        const yL2 = size2 % 256;
        const yH2 = Math.floor(size2 / 256);
        
        commands += '\x1D\x76\x30\x00'; // GS v 0
        commands += String.fromCharCode(xL2, xH2, yL2, yH2);
        
        for (let y = 0; y < size2; y++) {
          for (let x = 0; x < bytesPerLine2; x++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
              const pixelX = x * 8 + bit;
              if (pixelX < size2) {
                if (this.isQRPatternPixel(pixelX, y, size2)) {
                  byte |= (1 << (7 - bit));
                }
              }
            }
            commands += String.fromCharCode(byte);
          }
        }
        commands += '\x0A\x0A'; // Double line feed
        
        commands += '\n--- Raster QR Code Test Completed ---\n';
        commands += 'If you see QR-like patterns above, raster QR works.\n';
        commands += 'This bypasses ESC/POS QR commands entirely.\n';
        commands += '\x0A\x0A'; // Double line feed
        
        await this.sendRawCommands(commands);
        this.addTestResult(true, 'Raster QR code test printed successfully');
        
      } catch (error) {
        console.error('Raster QR code test failed:', error);
        this.addTestResult(false, `Raster QR code test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    // Helper method to create QR-like pattern
    isQRPatternPixel(x, y, size) {
      // Create a simple QR-like pattern with 3 corner squares
      const margin = 2;
      const cornerSize = Math.floor(size / 8);
      
      // Top-left corner
      if (x < cornerSize + margin && y < cornerSize + margin) {
        return (x < margin || y < margin || 
                (x >= cornerSize - margin && y >= cornerSize - margin) ||
                (x >= margin && x < cornerSize - margin && y >= margin && y < cornerSize - margin));
      }
      
      // Top-right corner
      if (x >= size - cornerSize - margin && y < cornerSize + margin) {
        const localX = x - (size - cornerSize - margin);
        return (localX < margin || y < margin || 
                (localX >= cornerSize - margin && y >= cornerSize - margin) ||
                (localX >= margin && localX < cornerSize - margin && y >= margin && y < cornerSize - margin));
      }
      
      // Bottom-left corner
      if (x < cornerSize + margin && y >= size - cornerSize - margin) {
        const localY = y - (size - cornerSize - margin);
        return (x < margin || localY < margin || 
                (x >= cornerSize - margin && localY >= cornerSize - margin) ||
                (x >= margin && x < cornerSize - margin && localY >= margin && localY < cornerSize - margin));
      }
      
      // Center area - create some pattern
      if (x >= size/3 && x < 2*size/3 && y >= size/3 && y < 2*size/3) {
        return (x + y) % 4 < 2;
      }
      
      // Border
      if (x < margin || y < margin || x >= size - margin || y >= size - margin) {
        return true;
      }
      
      return false;
    },

    async printHTMLTest() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting HTML print test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        const terminalInfo = this.$store.getters["config/terminal"];
        
        // Create HTML content with embedded images and QR codes
        const htmlContent = `
          <html>
            <head>
              <style>
                body { 
                  font-family: monospace; 
                  font-size: 12px; 
                  margin: 0; 
                  padding: 10px;
                  width: ${this.printerWidthPx};
                }
                .center { text-align: center; }
                .qr-code { 
                  display: inline-block; 
                  margin: 10px 0; 
                }
                .test-pattern {
                  width: 64px;
                  height: 64px;
                  border: 1px solid #000;
                  display: inline-block;
                  background: repeating-conic-gradient(#000 0deg 90deg, #fff 90deg 180deg);
                }
              </style>
            </head>
            <body>
              <div class="center">
                <h2>HTML Print Test</h2>
                <p>Testing HTML rendering with QZ Tray</p>
                
                <h3>Test Pattern</h3>
                <div class="test-pattern"></div>
                
                <h3>QR Code Test</h3>
                <div class="qr-code">
                  <svg width="64" height="64" viewBox="0 0 64 64">
                    <!-- Simple QR-like pattern -->
                    <rect x="0" y="0" width="64" height="64" fill="white"/>
                    <rect x="2" y="2" width="20" height="20" fill="black"/>
                    <rect x="4" y="4" width="16" height="16" fill="white"/>
                    <rect x="6" y="6" width="12" height="12" fill="black"/>
                    
                    <rect x="42" y="2" width="20" height="20" fill="black"/>
                    <rect x="44" y="4" width="16" height="16" fill="white"/>
                    <rect x="46" y="6" width="12" height="12" fill="black"/>
                    
                    <rect x="2" y="42" width="20" height="20" fill="black"/>
                    <rect x="4" y="44" width="16" height="16" fill="white"/>
                    <rect x="6" y="46" width="12" height="12" fill="black"/>
                    
                    <!-- Center pattern -->
                    <rect x="24" y="24" width="16" height="16" fill="black"/>
                    <rect x="26" y="26" width="12" height="12" fill="white"/>
                    <rect x="28" y="28" width="8" height="8" fill="black"/>
                  </svg>
                </div>
                
                <h3>Text Graphics</h3>
                <pre>
████████████████████████████████
█                                █
█  ████  ████  ████  ████  ████  █
█  ████  ████  ████  ████  ████  █
█                                █
█  ████  ████  ████  ████  ████  █
█  ████  ████  ████  ████  ████  █
█                                █
████████████████████████████████
                </pre>
                
                <h3>Barcode Test</h3>
                <div style="font-family: 'Courier New', monospace; font-size: 16px;">
                  ||| ||| ||| ||| ||| ||| ||| ||| ||| |||
                </div>
                
                <p><strong>If you can see this text, HTML printing works!</strong></p>
                <p>This bypasses ESC/POS commands entirely.</p>
              </div>
            </body>
          </html>
        `;

        // Create QZ configuration
        const config = qz.configs.create(terminalInfo.printer);
        
        // Data payload with HTML
        const data = [{
          type: 'html',
          format: 'plain',
          data: htmlContent
        }];

        this.addTestResult(false, 'Sending HTML content to QZ Tray...');
        this.addTestResult(false, `HTML content length: ${htmlContent.length} characters`);
        this.addTestResult(false, `Target printer: ${terminalInfo.printer}`);

        // Send the print job
        await qz.print(config, data);
        
        this.addTestResult(true, 'HTML print test completed successfully');
        
      } catch (error) {
        console.error('HTML print test failed:', error);
        this.addTestResult(false, `HTML print test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printHTMLWithRealQR() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting HTML with real QR code test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        const terminalInfo = this.$store.getters["config/terminal"];
        
        // Generate real QR code data URL
        const qrDataURL = await this.generateRealQRCode('Hello World!', 128);
        
        // Create HTML content with real QR code
        const htmlContent = `
          <html>
            <head>
              <style>
                body { 
                  font-family: monospace; 
                  font-size: 12px; 
                  margin: 0; 
                  padding: 10px;
                  width: ${this.printerWidthPx};
                }
                .center { text-align: center; }
                .qr-code { 
                  display: inline-block; 
                  margin: 10px 0; 
                }
                .test-pattern {
                  width: 64px;
                  height: 64px;
                  border: 1px solid #000;
                  display: inline-block;
                  background: repeating-conic-gradient(#000 0deg 90deg, #fff 90deg 180deg);
                }
              </style>
            </head>
            <body>
              <div class="center">
                <h2>HTML Print Test with Real QR Code</h2>
                <p>Testing HTML rendering with QZ Tray</p>
                
                <h3>QR Code - Small (64x64)</h3>
                <div class="qr-code">
                  <img src="${await this.generateRealQRCode('Hello World!', 64)}" alt="Small QR Code" style="width: 64px; height: 64px; image-rendering: pixelated;" />
                </div>
                
                <h3>QR Code - Medium (96x96)</h3>
                <div class="qr-code">
                  <img src="${await this.generateRealQRCode('Hello World!', 96)}" alt="Medium QR Code" style="width: 96px; height: 96px; image-rendering: pixelated;" />
                </div>
                
                <h3>QR Code - Large (128x128)</h3>
                <div class="qr-code">
                  <img src="${qrDataURL}" alt="Large QR Code" style="width: 128px; height: 128px; image-rendering: pixelated;" />
                </div>
                
                <h3>QR Code - Extra Large (192x192)</h3>
                <div class="qr-code">
                  <img src="${await this.generateRealQRCode('Hello World!', 192)}" alt="Extra Large QR Code" style="width: 192px; height: 192px; image-rendering: pixelated;" />
                </div>
                
                <h3>Simple Black/White Pattern</h3>
                <div class="qr-code">
                  <img src="${this.generateSimplePattern(48)}" alt="Simple Pattern" style="width: 48px; height: 48px; image-rendering: pixelated;" />
                </div>
                
                <h3>Test Pattern</h3>
                <div class="test-pattern"></div>
                
                <h3>Text Graphics</h3>
                <pre>
████████████████████████████████
█                                █
█  ████  ████  ████  ████  ████  █
█  ████  ████  ████  ████  ████  █
█                                █
█  ████  ████  ████  ████  ████  █
█  ████  ████  ████  ████  ████  █
█                                █
████████████████████████████████
                </pre>
                
                <p><strong>If you can see the QR code above, HTML printing works!</strong></p>
                <p>This uses a real QR code generated in JavaScript.</p>
              </div>
            </body>
          </html>
        `;

        // Create QZ configuration
        const config = qz.configs.create(terminalInfo.printer);
        
        // Data payload with HTML
        const data = [{
          type: 'html',
          format: 'plain',
          data: htmlContent
        }];

        this.addTestResult(false, 'Sending HTML with real QR code to QZ Tray...');
        this.addTestResult(false, `HTML content length: ${htmlContent.length} characters`);
        this.addTestResult(false, `Target printer: ${terminalInfo.printer}`);

        // Send the print job
        await qz.print(config, data);
        
        this.addTestResult(true, 'HTML with real QR code test completed successfully');
        
      } catch (error) {
        console.error('HTML with real QR code test failed:', error);
        this.addTestResult(false, `HTML with real QR code test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printBeautifulReceipt() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting beautiful receipt test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        const terminalInfo = this.$store.getters["config/terminal"];
        
        // Generate QR code for the receipt
        let qrDataURL;
        try {
          qrDataURL = await this.generateRealQRCode('https://biyo.com/receipt/12345', 80);
          this.addTestResult(false, 'QR code generated successfully');
        } catch (qrError) {
          this.addTestResult(false, `QR code generation failed: ${qrError.message}, using fallback`);
          qrDataURL = this.generateSimplePattern(80);
        }
        
        // Create beautiful receipt HTML content
        const htmlContent = `
          <html>
            <head>
              <style>
                body { 
                  font-family: 'Courier New', monospace; 
                  font-size: 11px; 
                  margin: 0; 
                  padding: 5px;
                  width: ${this.printerWidthPx};
                  background: white;
                  color: black;
                }
                .center { text-align: center; }
                .receipt {
                  width: 100%;
                  max-width: ${this.printerWidthPx};
                  margin: 0 auto;
                }
                .header {
                  text-align: center;
                  border-bottom: 1px dashed #333;
                  padding-bottom: 8px;
                  margin-bottom: 10px;
                }
                .logo {
                  width: 60px;
                  height: 50px;
                  margin: 0 auto 8px;
                  display: block;
                }
                .company-name {
                  font-size: 16px;
                  font-weight: bold;
                  margin-bottom: 4px;
                }
                .company-info {
                  font-size: 9px;
                  color: #666;
                  margin-bottom: 8px;
                }
                .receipt-title {
                  text-align: center;
                  font-size: 14px;
                  font-weight: bold;
                  margin: 10px 0;
                  text-decoration: underline;
                }
                .receipt-info {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
                  font-size: 10px;
                }
                .items {
                  margin: 10px 0;
                }
                .item {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 6px;
                  padding: 3px 0;
                  border-bottom: 1px dotted #ccc;
                }
                .item-name {
                  flex: 1;
                  font-weight: bold;
                  font-size: 10px;
                }
                .item-price {
                  font-weight: bold;
                  text-align: right;
                  font-size: 10px;
                }
                .item-details {
                  font-size: 8px;
                  color: #666;
                  margin-top: 1px;
                }
                .totals {
                  border-top: 2px solid #333;
                  padding-top: 8px;
                  margin-top: 10px;
                }
                .total-line {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 3px;
                  font-size: 10px;
                }
                .total-line.final {
                  font-size: 12px;
                  font-weight: bold;
                  border-top: 1px solid #333;
                  padding-top: 3px;
                  margin-top: 5px;
                }
                .qr-section {
                  text-align: center;
                  margin: 15px 0;
                  padding: 10px;
                  border: 1px dashed #333;
                }
                .qr-code {
                  margin: 5px 0;
                }
                .qr-text {
                  font-size: 8px;
                  color: #666;
                  margin-top: 3px;
                }
                .footer {
                  text-align: center;
                  margin-top: 15px;
                  padding-top: 8px;
                  border-top: 1px dashed #333;
                  font-size: 8px;
                  color: #666;
                }
                .thank-you {
                  font-size: 11px;
                  font-weight: bold;
                  margin: 10px 0;
                  text-align: center;
                }
                .divider {
                  text-align: center;
                  margin: 8px 0;
                  font-size: 12px;
                  color: #333;
                }
              </style>
            </head>
            <body>
              <div class="receipt">
                <div class="header">
                  <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPsAAACUCAMAAABiDq8bAAAAYFBMVEUAAAD////6+vrc3Nz29vbu7u7y8vLW1tY9PT2wsLC7u7vq6upVVVUTExPLy8t6enqTk5OBgYEnJyehoaFQUFBJSUlubm4iIiKHh4caGhpzc3NjY2PDw8NERETk5OQxMTHPYAHGAAAED0lEQVR4nO3a54KzKhAG4A87Vgy2RNT7v8tj2m6MxpIokj3v8393mADDiP77BwAAAAAAAAAAAAAAAAAAAAAAAAAAAPAex9l7BK80W4+sjDYO8I7g0GSUJxtHKWm1cYSlgqYquV3zYOtAaci3DrHIIclYaJmhhMV4EPph+ygzHdMspjUhOvVlhHMJkxFmBidjNLQIsQSTU3s58XIpgcYdyzZvj7RqGkkaECOEyok0oipoqGvnxEnISllRC4Nojaxgg/KMh7p5yZtobpRKC5y1q0yXFq0vZWFtXie83ebclzgNqd2GjOXF6zhGwrPueRPiRc1RYvRcnINKDPgrOR9kD8Kt+7dn9BxV9pIPAofpnbyJRWXO+AW7BJZ5uB+b6uRa3cQNnW3eufZk1+Iqq7AGTpLxpwlvd3l4khT/0aXOydrujX9iofWcuKG7UjrXZ4F7++G3j+RHzK2f8ybEDCV1rj3sXmC3DeNEnNpGP/G2c433aqWT+3jc7WL8tug9NivlF7i7n6Kz1aN7WrhhPTThbecqTvI61z5ONs39RPW6V9luLF7u+gCRGtvlnjLbM7QXiZO6qKT3MV0Px+yqD7BBJMyXaZ+3+Y67/IY9jG+9Ou9TcyTt88+878PyRfN42For/MO8SZ5b9Geap8bFGO2M6sN7ytzxY/Gqrt1JuXOdo+z2WB/MR1CVEZ2Y8HbK63061yHdaX+7qXXKmIfDB3h3yrk677vK54nKlv+PY1lwMdCi99RurM7d/2Nbc7P08qKKuatPT3jL5m/8rhtK7N4QFxzxx4wLe+Iou9FEIfseakrcH6VRzPvTlAnbG2tdHli0VGeb3zTuwEDN6eTzyK29WQv9zCtSFd71PPEHj2Jj/KBLuP66Q++zS5Xq26+BJX8RvlqhAesXiDGaq9xav2nCl4MWft550AiOuRO7s5f5NXFvhzvXuaqxXCy3KNO0aqVpxoavmsZY+h53rrOVM+bOWJrz9c9qoUznOqzX2KzEshXqXIcFr7f7J2q3ULOyP8rntWTLtJ3rztdQs+TrZy4Kxbf53WHlxC16Un2b/6hWzdxjvvrb/EeyYub2qVG3jxmwXu7unm9W3tKsk7im1jeo86xR5w1dkTvXhT4+3zVPSPv0b2WB+CjztnNV7WPzBYoPMvfEF3SuI6oF1y9dOlX6AXWGw9B13TQtZF/SuY7J3ph4g56+eJv/yul0rl3f1bmO8iffHXbYkfNVneu4eMGVlEi+4cl8ATZzy5vsr6z1B8V0d6cZ4bf2bxN6r6G7DNNW4wORTQRD37deWfpun7nK4rt2/82cqYe0+OOJXzgFFXZtGZfdbda2oDz2/9B5NuHgZ3HBWkVUJn+wqAMAAAAAAAAAAAAAAAAAAAAAAMD/2X8+lS8Dw21MzQAAAABJRU5ErkJggg==" alt="Logo" class="logo">
                  <div class="company-name">BİYO WEB POS</div>
                  <div class="company-info">
                    123 Main Street<br>
                    New York, NY 10001<br>
                    Phone: (555) 123-4567<br>
                    Email: info@biyo.com
                  </div>
                </div>

                <div class="receipt-title">SALES RECEIPT</div>

                <div class="receipt-info">
                  <div>Receipt #12345</div>
                  <div>Date: ${new Date().toLocaleDateString()}</div>
                </div>

                <div class="receipt-info">
                  <div>Time: ${new Date().toLocaleTimeString()}</div>
                  <div>Cashier: John Smith</div>
                </div>

                <div class="items">
                  <div class="item">
                    <div>
                      <div class="item-name">Cappuccino</div>
                      <div class="item-details">Large, with milk</div>
                    </div>
                    <div class="item-price">$4.50</div>
                  </div>
                  
                  <div class="item">
                    <div>
                      <div class="item-name">Croissant</div>
                      <div class="item-details">Buttery, fresh</div>
                    </div>
                    <div class="item-price">$3.00</div>
                  </div>
                  
                  <div class="item">
                    <div>
                      <div class="item-name">Americano</div>
                      <div class="item-details">Medium, black</div>
                    </div>
                    <div class="item-price">$3.75</div>
                  </div>
                  
                  <div class="item">
                    <div>
                      <div class="item-name">Water (0.5L)</div>
                      <div class="item-details">Natural spring water</div>
                    </div>
                    <div class="item-price">$1.50</div>
                  </div>
                </div>

                <div class="totals">
                  <div class="total-line">
                    <span>Subtotal:</span>
                    <span>$12.75</span>
                  </div>
                  <div class="total-line">
                    <span>Tax (8.5%):</span>
                    <span>$1.08</span>
                  </div>
                  <div class="total-line">
                    <span>Discount:</span>
                    <span>-$1.00</span>
                  </div>
                  <div class="total-line final">
                    <span>TOTAL:</span>
                    <span>$12.83</span>
                  </div>
                </div>

                <div class="qr-section">
                  <div class="qr-code">
                    <img src="${qrDataURL}" alt="QR Code" style="width: 80px; height: 80px; image-rendering: pixelated;" />
                  </div>
                  <div class="qr-text">
                    Scan QR code for online receipt
                  </div>
                </div>

                <div class="thank-you">
                  Thank You!
                </div>

                <div class="divider">••••••••••••••••••••••••••••••••</div>

                <div class="footer">
                  <div>This receipt is stored electronically</div>
                  <div>Receipt number: #12345</div>
                  <div>www.biyo.com</div>
                </div>
              </div>
            </body>
          </html>
        `;

        // Create QZ configuration using the working approach
        const config = qz.configs.create(terminalInfo.printer);
        
        // Data payload with HTML using the working structure
        const data = [{
          type: 'html',
          format: 'plain',
          data: htmlContent
        }];

        this.addTestResult(false, 'Sending beautiful receipt to QZ Tray...');
        this.addTestResult(false, `HTML content length: ${htmlContent.length} characters`);
        this.addTestResult(false, `Target printer: ${terminalInfo.printer}`);

        // Send the print job
        await qz.print(config, data);
        
        this.addTestResult(true, 'Beautiful receipt printed successfully!');

      } catch (error) {
        console.error('Beautiful receipt test failed:', error);
        this.addTestResult(false, `Beautiful receipt test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printModernReceipt() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting modern receipt test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        const terminalInfo = this.$store.getters["config/terminal"];
        
        // Generate QR code for the receipt
        let qrDataURL;
        try {
          qrDataURL = await this.generateRealQRCode('https://biyo.com/receipt/67890', 90);
          this.addTestResult(false, 'QR code generated successfully');
        } catch (qrError) {
          this.addTestResult(false, `QR code generation failed: ${qrError.message}, using fallback`);
          qrDataURL = this.generateSimplePattern(90);
        }
        
        // Create modern receipt HTML content
        const htmlContent = `
          <html>
            <head>
              <style>
                body { 
                  font-family: 'Arial', sans-serif; 
                  font-size: 10px; 
                  margin: 0; 
                  padding: 0;
                  width: ${this.printerWidthPx};
                  background: white;
                  color: black;
                }
                .modern-receipt {
                  width: 100%;
                  max-width: ${this.printerWidthPx};
                  margin: 0 auto;
                  background: white;
                  color: black;
                  padding: 15px;
                  border: 1px solid black;
                }
                .header-modern {
                  text-align: center;
                  margin-bottom: 20px;
                }
                .logo-modern {
                  width: 70px;
                  height: 70px;
                  margin: 0 auto 10px;
                  display: block;
                  border: 2px solid black;
                }
                .company-name-modern {
                  font-size: 18px;
                  font-weight: 300;
                  margin-bottom: 5px;
                  letter-spacing: 2px;
                }
                .company-tagline {
                  font-size: 8px;
                  opacity: 0.8;
                  margin-bottom: 15px;
                }
                .receipt-title-modern {
                  text-align: center;
                  font-size: 16px;
                  font-weight: 600;
                  margin: 20px 0;
                  text-transform: uppercase;
                  letter-spacing: 1px;
                }
                .receipt-info-modern {
                  border: 1px solid black;
                  padding: 10px;
                  margin-bottom: 15px;
                  display: flex;
                  justify-content: space-between;
                  font-size: 9px;
                }
                .items-modern {
                  border: 1px solid black;
                  padding: 10px;
                  margin: 15px 0;
                }
                .item-modern {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 8px;
                  padding: 5px 0;
                  border-bottom: 1px solid black;
                }
                .item-name-modern {
                  flex: 1;
                  font-weight: 500;
                  font-size: 9px;
                }
                .item-price-modern {
                  font-weight: 600;
                  text-align: right;
                  font-size: 9px;
                }
                .item-details-modern {
                  font-size: 7px;
                  color: #666;
                  margin-top: 2px;
                }
                .totals-modern {
                  border: 1px solid black;
                  padding: 12px;
                  margin-top: 15px;
                }
                .total-line-modern {
                  display: flex;
                  justify-content: space-between;
                  margin-bottom: 4px;
                  font-size: 9px;
                }
                .total-line-modern.final {
                  font-size: 12px;
                  font-weight: 700;
                  border-top: 2px solid black;
                  padding-top: 6px;
                  margin-top: 8px;
                }
                .qr-section-modern {
                  text-align: center;
                  margin: 20px 0;
                  padding: 15px;
                  border: 1px solid black;
                }
                .qr-code-modern {
                  margin: 8px 0;
                }
                .qr-text-modern {
                  font-size: 7px;
                  color: #666;
                  margin-top: 5px;
                }
                .footer-modern {
                  text-align: center;
                  margin-top: 20px;
                  padding-top: 15px;
                  border-top: 1px solid black;
                  font-size: 7px;
                  color: #666;
                }
                .thank-you-modern {
                  font-size: 12px;
                  font-weight: 600;
                  margin: 15px 0;
                  text-align: center;
                  text-transform: uppercase;
                  letter-spacing: 1px;
                }
                .divider-modern {
                  text-align: center;
                  margin: 10px 0;
                  font-size: 14px;
                  color: #666;
                }
                .status-badge {
                  display: inline-block;
                  border: 1px solid black;
                  color: black;
                  padding: 2px 8px;
                  font-size: 7px;
                  font-weight: 600;
                  text-transform: uppercase;
                  margin-bottom: 10px;
                }
              </style>
            </head>
            <body>
              <div class="modern-receipt">
                <div class="header-modern">
                  <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPsAAACUCAMAAABiDq8bAAAAYFBMVEUAAAD////6+vrc3Nz29vbu7u7y8vLW1tY9PT2wsLC7u7vq6upVVVUTExPLy8t6enqTk5OBgYEnJyehoaFQUFBJSUlubm4iIiKHh4caGhpzc3NjY2PDw8NERETk5OQxMTHPYAHGAAAED0lEQVR4nO3a54KzKhAG4A87Vgy2RNT7v8tj2m6MxpIokj3v8393mADDiP77BwAAAAAAAAAAAAAAAAAAAAAAAAAAAPAex9l7BK80W4+sjDYO8I7g0GSUJxtHKWm1cYSlgqYquV3zYOtAaci3DrHIIclYaJmhhMV4EPph+ygzHdMspjUhOvVlhHMJkxFmBidjNLQIsQSTU3s58XIpgcYdyzZvj7RqGkkaECOEyok0oipoqGvnxEnISllRC4Nojaxgg/KMh7p5yZtobpRKC5y1q0yXFq0vZWFtXie83ebclzgNqd2GjOXF6zhGwrPueRPiRc1RYvRcnINKDPgrOR9kD8Kt+7dn9BxV9pIPAofpnbyJRWXO+AW7BJZ5uB+b6uRa3cQNnW3eufZk1+Iqq7AGTpLxpwlvd3l4khT/0aXOydrujX9iofWcuKG7UjrXZ4F7++G3j+RHzK2f8ybEDCV1rj3sXmC3DeNEnNpGP/G2c433aqWT+3jc7WL8tug9NivlF7i7n6Kz1aN7WrhhPTThbecqTvI61z5ONs39RPW6V9luLF7u+gCRGtvlnjLbM7QXiZO6qKT3MV0Px+yqD7BBJMyXaZ+3+Y67/IY9jG+9Ou9TcyTt88+878PyRfN42For/MO8SZ5b9Geap8bFGO2M6sN7ytzxY/Gqrt1JuXOdo+z2WB/MR1CVEZ2Y8HbK63061yHdaX+7qXXKmIfDB3h3yrk677vK54nKlv+PY1lwMdCi99RurM7d/2Nbc7P08qKKuatPT3jL5m/8rhtK7N4QFxzxx4wLe+Iou9FEIfseakrcH6VRzPvTlAnbG2tdHli0VGeb3zTuwEDN6eTzyK29WQv9zCtSFd71PPEHj2Jj/KBLuP66Q++zS5Xq26+BJX8RvlqhAesXiDGaq9xav2nCl4MWft550AiOuRO7s5f5NXFvhzvXuaqxXCy3KNO0aqVpxoavmsZY+h53rrOVM+bOWJrz9c9qoUznOqzX2KzEshXqXIcFr7f7J2q3ULOyP8rntWTLtJ3rztdQs+TrZy4Kxbf53WHlxC16Un2b/6hWzdxjvvrb/EeyYub2qVG3jxmwXu7unm9W3tKsk7im1jeo86xR5w1dkTvXhT4+3zVPSPv0b2WB+CjztnNV7WPzBYoPMvfEF3SuI6oF1y9dOlX6AXWGw9B13TQtZF/SuY7J3ph4g56+eJv/yul0rl3f1bmO8iffHXbYkfNVneu4eMGVlEi+4cl8ATZzy5vsr6z1B8V0d6cZ4bf2bxN6r6G7DNNW4wORTQRD37deWfpun7nK4rt2/82cqYe0+OOJXzgFFXZtGZfdbda2oDz2/9B5NuHgZ3HBWkVUJn+wqAMAAAAAAAAAAAAAAAAAAAAAAMD/2X8+lS8Dw21MzQAAAABJRU5ErkJggg==" alt="Logo" class="logo-modern">
                  <div class="company-name-modern">BİYO WEB POS</div>
                  <div class="company-tagline">Digital Commerce Solutions</div>
                  <div class="status-badge">PAID</div>
                </div>

                <div class="receipt-title-modern">Transaction Receipt</div>

                <div class="receipt-info-modern">
                  <div>
                    <div>Order #67890</div>
                    <div>${new Date().toLocaleDateString()}</div>
                  </div>
                  <div>
                    <div>${new Date().toLocaleTimeString()}</div>
                    <div>Cashier: Sarah</div>
                  </div>
                </div>

                <div class="items-modern">
                  <div class="item-modern">
                    <div>
                      <div class="item-name-modern">Espresso Macchiato</div>
                      <div class="item-details-modern">Double shot, oat milk</div>
                    </div>
                    <div class="item-price-modern">$5.25</div>
                  </div>
                  
                  <div class="item-modern">
                    <div>
                      <div class="item-name-modern">Avocado Toast</div>
                      <div class="item-details-modern">Sourdough, organic</div>
                    </div>
                    <div class="item-price-modern">$8.50</div>
                  </div>
                  
                  <div class="item-modern">
                    <div>
                      <div class="item-name-modern">Cold Brew</div>
                      <div class="item-details-modern">Nitro, 16oz</div>
                    </div>
                    <div class="item-price-modern">$4.75</div>
                  </div>
                  
                  <div class="item-modern">
                    <div>
                      <div class="item-name-modern">Green Tea</div>
                      <div class="item-details-modern">Jasmine, loose leaf</div>
                    </div>
                    <div class="item-price-modern">$3.25</div>
                  </div>
                </div>

                <div class="totals-modern">
                  <div class="total-line-modern">
                    <span>Subtotal</span>
                    <span>$21.75</span>
                  </div>
                  <div class="total-line-modern">
                    <span>Tax (9.25%)</span>
                    <span>$2.01</span>
                  </div>
                  <div class="total-line-modern">
                    <span>Service Fee</span>
                    <span>$1.50</span>
                  </div>
                  <div class="total-line-modern final">
                    <span>TOTAL</span>
                    <span>$25.26</span>
                  </div>
                </div>

                <div class="qr-section-modern">
                  <div class="qr-code-modern">
                    <img src="${qrDataURL}" alt="QR Code" style="width: 90px; height: 90px; image-rendering: pixelated;" />
                  </div>
                  <div class="qr-text-modern">
                    Scan for digital receipt
                  </div>
                </div>

                <div class="thank-you-modern">
                  Thank You!
                </div>

                <div class="divider-modern">◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆ ◆</div>

                <div class="footer-modern">
                  <div>Receipt stored in cloud</div>
                  <div>Order #67890 • www.biyo.com</div>
                  <div>Questions? Contact support</div>
                </div>
              </div>
            </body>
          </html>
        `;

        // Create QZ configuration using the working approach
        const config = qz.configs.create(terminalInfo.printer);
        
        // Data payload with HTML using the working structure
        const data = [{
          type: 'html',
          format: 'plain',
          data: htmlContent
        }];

        this.addTestResult(false, 'Sending modern receipt to QZ Tray...');
        this.addTestResult(false, `HTML content length: ${htmlContent.length} characters`);
        this.addTestResult(false, `Target printer: ${terminalInfo.printer}`);

        // Send the print job
        await qz.print(config, data);
        
        this.addTestResult(true, 'Modern receipt printed successfully!');

      } catch (error) {
        console.error('Modern receipt test failed:', error);
        this.addTestResult(false, `Modern receipt test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    // Generate QR code as data URL
    generateQRCodeDataURL(text, size = 64) {
      try {
        // Create a simple QR code pattern using canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = size;
        canvas.height = size;
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Create a simple QR-like pattern optimized for thermal printers
        const cellSize = Math.max(2, size / 21); // 21x21 grid, minimum 2px cells
        const gridSize = Math.floor(size / cellSize);
        
        // Draw border (thicker for thermal printers)
        ctx.fillStyle = 'black';
        const borderWidth = Math.max(1, cellSize / 2);
        ctx.fillRect(0, 0, size, borderWidth);
        ctx.fillRect(0, 0, borderWidth, size);
        ctx.fillRect(size - borderWidth, 0, borderWidth, size);
        ctx.fillRect(0, size - borderWidth, size, borderWidth);
        
        // Draw corner squares (larger for thermal printers)
        const cornerSize = cellSize * 7;
        this.drawCornerSquare(ctx, cellSize, cellSize, cornerSize, cornerSize);
        this.drawCornerSquare(ctx, size - cellSize - cornerSize, cellSize, cornerSize, cornerSize);
        this.drawCornerSquare(ctx, cellSize, size - cellSize - cornerSize, cornerSize, cornerSize);
        
        // Draw center pattern
        const centerSize = cellSize * 5;
        const centerX = size / 2 - centerSize / 2;
        const centerY = size / 2 - centerSize / 2;
        this.drawCornerSquare(ctx, centerX, centerY, centerSize, centerSize);
        
        // Add data pattern based on text (more dense for thermal printers)
        ctx.fillStyle = 'black';
        for (let i = 0; i < text.length; i++) {
          const charCode = text.charCodeAt(i);
          // Create a more regular pattern
          for (let j = 0; j < 3; j++) {
            const x = (charCode * (7 + j)) % (gridSize - 2) + 1;
            const y = (charCode * (11 + j)) % (gridSize - 2) + 1;
            ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
          }
        }
        
        // Add some alignment patterns
        for (let i = 0; i < 3; i++) {
          for (let j = 0; j < 3; j++) {
            if ((i + j) % 2 === 0) {
              const x = (i * 6 + 2) * cellSize;
              const y = (j * 6 + 2) * cellSize;
              ctx.fillRect(x, y, cellSize, cellSize);
            }
          }
        }
        
        return canvas.toDataURL('image/png');
        
      } catch (error) {
        console.error('QR code generation failed:', error);
        // Return a simple fallback pattern
        return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
      }
    },

    // Helper method to draw corner squares
    drawCornerSquare(ctx, x, y, width, height) {
      // Outer square
      ctx.fillStyle = 'black';
      ctx.fillRect(x, y, width, height);
      
      // Inner white square
      ctx.fillStyle = 'white';
      ctx.fillRect(x + width/7, y + height/7, width * 5/7, height * 5/7);
      
      // Inner black square
      ctx.fillStyle = 'black';
      ctx.fillRect(x + width * 2/7, y + height * 2/7, width * 3/7, height * 3/7);
    },

    // Generate a simple black/white pattern for testing
    generateSimplePattern(size = 48) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = size;
        canvas.height = size;
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Create a simple checkerboard pattern
        const cellSize = size / 8;
        
        ctx.fillStyle = 'black';
        for (let y = 0; y < 8; y++) {
          for (let x = 0; x < 8; x++) {
            if ((x + y) % 2 === 0) {
              ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
            }
          }
        }
        
        return canvas.toDataURL('image/png');
        
      } catch (error) {
        console.error('Simple pattern generation failed:', error);
        return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
      }
    },

    // Generate real QR code using proper library
    async generateRealQRCode(text, size = 128) {
      try {
        this.addTestResult(false, `Generating real QR code for: "${text}"`);
        
        // Try to load QR code library from CDN
        await this.loadQRCodeLibrary();
        
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = size;
        canvas.height = size;
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Try different QR code libraries
        let qrDataURL;
        
        // Method 1: Try qrcode-generator library (most reliable)
        if (typeof window.qrcode !== 'undefined' && window.qrcode) {
          this.addTestResult(false, 'Using qrcode-generator library...');
          try {
            const qr = window.qrcode(0, 'M');
            if (!qr) {
              throw new Error('QR code object is null');
            }
            
            qr.addData(text);
            qr.make();
            
            const moduleCount = qr.getModuleCount ? qr.getModuleCount() : 21;
            this.addTestResult(false, `QR code generated: ${moduleCount}x${moduleCount} modules`);
            
            const cellSize = Math.floor(size / moduleCount);
            const offset = Math.floor((size - moduleCount * cellSize) / 2);
            
            for (let row = 0; row < moduleCount; row++) {
              for (let col = 0; col < moduleCount; col++) {
                if (qr.isDark && qr.isDark(row, col)) {
                  ctx.fillStyle = '#000000';
                  ctx.fillRect(col * cellSize + offset, row * cellSize + offset, cellSize, cellSize);
                }
              }
            }
            
            qrDataURL = canvas.toDataURL('image/png');
            this.addTestResult(false, 'qrcode-generator library successful');
          } catch (qrgError) {
            this.addTestResult(false, `qrcode-generator failed: ${qrgError.message}`);
          }
        }
        
        // Method 2: Try qrcode.js library
        if (!qrDataURL && typeof window.QRCode !== 'undefined' && window.QRCode) {
          this.addTestResult(false, 'Using QRCode.js library...');
          try {
            const qr = new window.QRCode({
              text: text,
              width: size,
              height: size,
              colorDark: '#000000',
              colorLight: '#FFFFFF',
              correctLevel: window.QRCode.CorrectLevel ? window.QRCode.CorrectLevel.L : 0
            });
            
            if (qr && qr.toDataURL) {
              qrDataURL = qr.toDataURL();
              this.addTestResult(false, 'QRCode.js library successful');
            } else {
              throw new Error('QRCode.js object is invalid');
            }
          } catch (qrError) {
            this.addTestResult(false, `QRCode.js failed: ${qrError.message}`);
          }
        }
        
        if (qrDataURL) {
          this.addTestResult(false, 'QR code generation successful');
          return qrDataURL;
        } else {
          throw new Error('No QR code library available');
        }
        
      } catch (error) {
        console.error('Real QR code generation failed:', error);
        this.addTestResult(false, `QR code generation failed: ${error.message}, using fallback`);
        // Fallback to simple pattern
        return this.generateSimplePattern(size);
      }
    },

    // Built-in QR code generator
    generateBuiltInQRCode(text, size, ctx) {
      // Create a 21x21 QR code pattern (version 1)
      const moduleCount = 21;
      const cellSize = Math.floor(size / moduleCount);
      const offset = Math.floor((size - moduleCount * cellSize) / 2);
      
      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      
      // Create QR code pattern
      const pattern = this.createQRPattern(text, moduleCount);
      
      // Draw the pattern
      ctx.fillStyle = 'black';
      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (pattern[row][col]) {
            ctx.fillRect(
              col * cellSize + offset, 
              row * cellSize + offset, 
              cellSize, 
              cellSize
            );
          }
        }
      }
      
      return ctx.canvas.toDataURL('image/png');
    },

    // Create QR code pattern
    createQRPattern(text, moduleCount) {
      const pattern = Array(moduleCount).fill().map(() => Array(moduleCount).fill(false));
      
      // Add finder patterns (corner squares)
      this.addFinderPattern(pattern, 0, 0, moduleCount);
      this.addFinderPattern(pattern, moduleCount - 7, 0, moduleCount);
      this.addFinderPattern(pattern, 0, moduleCount - 7, moduleCount);
      
      // Add separator patterns
      this.addSeparatorPattern(pattern, 0, 0, moduleCount);
      this.addSeparatorPattern(pattern, moduleCount - 8, 0, moduleCount);
      this.addSeparatorPattern(pattern, 0, moduleCount - 8, moduleCount);
      
      // Add timing patterns
      this.addTimingPattern(pattern, moduleCount);
      
      // Add dark module
      pattern[moduleCount - 8][8] = true;
      
      // Add data patterns based on text
      this.addDataPattern(pattern, text, moduleCount);
      
      return pattern;
    },

    // Add finder pattern (7x7 square with 3x3 inner square)
    addFinderPattern(pattern, startRow, startCol, moduleCount) {
      // Outer 7x7 square
      for (let row = 0; row < 7; row++) {
        for (let col = 0; col < 7; col++) {
          if (startRow + row < moduleCount && startCol + col < moduleCount) {
            pattern[startRow + row][startCol + col] = true;
          }
        }
      }
      
      // Inner 3x3 square (white)
      for (let row = 2; row < 5; row++) {
        for (let col = 2; col < 5; col++) {
          if (startRow + row < moduleCount && startCol + col < moduleCount) {
            pattern[startRow + row][startCol + col] = false;
          }
        }
      }
      
      // Center 1x1 square (black)
      if (startRow + 3 < moduleCount && startCol + 3 < moduleCount) {
        pattern[startRow + 3][startCol + 3] = true;
      }
    },

    // Add separator pattern (1 module wide border around finder)
    addSeparatorPattern(pattern, startRow, startCol, moduleCount) {
      // Top and bottom borders
      for (let col = 0; col < 8; col++) {
        if (startRow - 1 >= 0 && startCol + col < moduleCount) {
          pattern[startRow - 1][startCol + col] = false;
        }
        if (startRow + 7 < moduleCount && startCol + col < moduleCount) {
          pattern[startRow + 7][startCol + col] = false;
        }
      }
      
      // Left and right borders
      for (let row = 0; row < 8; row++) {
        if (startRow + row < moduleCount && startCol - 1 >= 0) {
          pattern[startRow + row][startCol - 1] = false;
        }
        if (startRow + row < moduleCount && startCol + 7 < moduleCount) {
          pattern[startRow + row][startCol + 7] = false;
        }
      }
    },

    // Add timing pattern (alternating black/white modules)
    addTimingPattern(pattern, moduleCount) {
      for (let i = 8; i < moduleCount - 8; i++) {
        if (i % 2 === 0) {
          pattern[6][i] = true;
          pattern[i][6] = true;
        }
      }
    },

    // Add data pattern based on text
    addDataPattern(pattern, text, moduleCount) {
      // Create a simple data pattern based on text hash
      let hash = 0;
      for (let i = 0; i < text.length; i++) {
        hash = ((hash << 5) - hash + text.charCodeAt(i)) & 0xffffffff;
      }
      
      // Add data modules in available spaces
      const dataModules = [
        [2, 2], [3, 2], [4, 2], [5, 2],
        [2, 3], [3, 3], [4, 3], [5, 3],
        [2, 4], [3, 4], [4, 4], [5, 4],
        [2, 5], [3, 5], [4, 5], [5, 5],
        [9, 2], [10, 2], [11, 2], [12, 2],
        [9, 3], [10, 3], [11, 3], [12, 3],
        [9, 4], [10, 4], [11, 4], [12, 4],
        [9, 5], [10, 5], [11, 5], [12, 5],
        [2, 9], [3, 9], [4, 9], [5, 9],
        [2, 10], [3, 10], [4, 10], [5, 10],
        [2, 11], [3, 11], [4, 11], [5, 11],
        [2, 12], [3, 12], [4, 12], [5, 12],
        [9, 9], [10, 9], [11, 9], [12, 9],
        [9, 10], [10, 10], [11, 10], [12, 10],
        [9, 11], [10, 11], [11, 11], [12, 11],
        [9, 12], [10, 12], [11, 12], [12, 12]
      ];
      
      for (let i = 0; i < dataModules.length; i++) {
        const [row, col] = dataModules[i];
        if (row < moduleCount && col < moduleCount && !pattern[row][col]) {
          // Use hash to determine if module should be black
          const bit = (hash >> (i % 32)) & 1;
          pattern[row][col] = bit === 1;
        }
      }
    },

    // Load QR code library from CDN
    async loadQRCodeLibrary() {
      return new Promise((resolve, reject) => {
        if (typeof window.QRCode !== 'undefined' || typeof window.qrcode !== 'undefined' || typeof window.qr !== 'undefined') {
          this.addTestResult(false, 'QR code library already loaded');
          resolve();
          return;
        }
        
        this.addTestResult(false, 'Loading QR code library from CDN...');
        
        // Try multiple QR code libraries
        const libraries = [
          {
            url: 'https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js',
            name: 'qrcode-generator (cdnjs)',
            check: () => typeof window.qrcode !== 'undefined'
          },
          {
            url: 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/js/qrcode.js',
            name: 'qrcode-generator',
            check: () => typeof window.qrcode !== 'undefined'
          },
          {
            url: 'https://unpkg.com/qrcode-generator@1.4.4/js/qrcode.js',
            name: 'qrcode-generator (unpkg)',
            check: () => typeof window.qrcode !== 'undefined'
          },
          {
            url: 'https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js',
            name: 'qrcode.js',
            check: () => typeof window.QRCode !== 'undefined'
          }
        ];
        
        let currentIndex = 0;
        
        const tryNextLibrary = () => {
          if (currentIndex >= libraries.length) {
            this.addTestResult(false, 'All QR code libraries failed to load');
            reject(new Error('Failed to load any QR code library'));
            return;
          }
          
          const library = libraries[currentIndex];
          this.addTestResult(false, `Trying ${library.name}...`);
          
          const script = document.createElement('script');
          script.src = library.url;
          script.onload = () => {
            // Check if the library actually loaded
            if (library.check()) {
              this.addTestResult(false, `${library.name} loaded successfully`);
              this.addTestResult(false, `QRCode type: ${typeof window.QRCode}`);
              this.addTestResult(false, `qrcode type: ${typeof window.qrcode}`);
              this.addTestResult(false, `qr type: ${typeof window.qr}`);
              resolve();
            } else {
              this.addTestResult(false, `${library.name} loaded but not available`);
              currentIndex++;
              tryNextLibrary();
            }
          };
          script.onerror = () => {
            this.addTestResult(false, `${library.name} failed to load`);
            currentIndex++;
            tryNextLibrary();
          };
          document.head.appendChild(script);
        };
        
        tryNextLibrary();
      });
    },

    async sendRawCommands(commands) {
      const terminalInfo = this.$store.getters["config/terminal"];
      
      // Format data for QZ Tray - similar to how Printer class does it
      // Add paper cut commands after the main commands
      const data = [];
      data.push(commands);
      data.push('\x0A\x0A\x0A');  // Line feeds
      data.push('\x1B\x69');      // Paper cut command
      
      this.addTestResult(false, `sendRawCommands: Sending ${data.length} array items to QZ Tray`);
      this.addTestResult(false, `Item 0: ${commands.length} bytes of ESC/POS`);
      this.addTestResult(false, `Item 1: Line feeds (3 newlines)`);
      this.addTestResult(false, `Item 2: Paper cut command`);
      
      await sendToPrinter(data, terminalInfo.printer);
    },



    async testQZTrayConnection() {
      try {
        this.addTestResult(false, 'Testing QZ Tray connection...');
        
        // Import QZ Tray
        const qz = await import('qz-tray');
        
        // Check if QZ Tray is active
        if (qz.default.websocket.isActive()) {
          this.addTestResult(true, 'QZ Tray is connected');
          
          // Try to get available printers
          try {
            const printers = await qz.default.printers.find();
            this.addTestResult(false, `Found ${printers.length} printers:`);
            printers.forEach((printer, index) => {
              this.addTestResult(false, `${index + 1}. ${printer.name} (${printer.connection})`);
            });
            
            // Check if XP-80C is in the list
            const xp80c = printers.find(p => p.name.includes('XP-80C') || p.name.includes('XP80C'));
            if (xp80c) {
              this.addTestResult(true, `Found XP-80C: ${xp80c.name}`);
            } else {
              this.addTestResult(false, 'XP-80C not found in printer list');
            }
            
          } catch (printerError) {
            this.addTestResult(false, `Could not list printers: ${printerError.message}`);
          }
          
        } else {
          this.addTestResult(false, 'QZ Tray is not connected');
          
          // Try to connect
          try {
            await qz.default.websocket.connect();
            this.addTestResult(true, 'Successfully connected to QZ Tray');
          } catch (connectError) {
            this.addTestResult(false, `Failed to connect to QZ Tray: ${connectError.message}`);
          }
        }
        
      } catch (error) {
        this.addTestResult(false, `QZ Tray test failed: ${error.message}`);
      }
    },

    async printMixedTest() {
      this.isPrinting = true;
      this.addTestResult(false, 'Starting mixed ESC/POS + HTML test...');
      this.addTestResult(false, `Using printer width: ${this.printerWidth} (${this.printerWidthPx})`);

      try {
        await this.ensureQZTrayConnection();
        
        // Generate QR code for HTML step
        const qrDataURL = await this.generateRealQRCode('https://biyo.com/mixed-test', 100);
        
        // Create combined HTML content with all steps
        const combinedHTMLContent = `
          <html>
            <head>
              <style>
                body { 
                  font-family: 'Arial', sans-serif; 
                  font-size: 12px; 
                  margin: 0; 
                  padding: 0;
                  width: ${this.printerWidthPx};
                  background: white;
                  color: black;
                }
                .mixed-receipt {
                  width: 100%;
                  max-width: ${this.printerWidthPx};
                  margin: 0 auto;
                  background: white;
                  color: black;
                  padding: 10px;
                  border: 1px solid black;
                }
                .header-mixed {
                  text-align: center;
                  margin-bottom: 15px;
                }
                .logo-mixed {
                  width: 80px;
                  height: 60px;
                  margin: 0 auto 10px;
                  display: block;
                  border: 2px solid black;
                }
                .company-name-mixed {
                  font-size: 16px;
                  font-weight: bold;
                  margin-bottom: 5px;
                }
                .step-title {
                  font-size: 14px;
                  font-weight: bold;
                  text-align: center;
                  margin: 10px 0;
                  border-top: 1px solid black;
                  border-bottom: 1px solid black;
                  padding: 5px 0;
                }
                .content-mixed {
                  margin: 10px 0;
                  line-height: 1.4;
                }
                .qr-section {
                  text-align: center;
                  margin: 15px 0;
                  padding: 10px;
                  border: 1px solid black;
                }
                .qr-code {
                  margin: 10px 0;
                }
                .qr-text {
                  font-size: 10px;
                  color: #666;
                  margin-top: 5px;
                }
                .summary {
                  margin: 15px 0;
                  padding: 10px;
                  border: 1px solid black;
                  background: #f9f9f9;
                }
              </style>
            </head>
            <body>
              <div class="mixed-receipt">
                <div class="header-mixed">
                  <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAPsAAACUCAMAAABiDq8bAAAAYFBMVEUAAAD////6+vrc3Nz29vbu7u7y8vLW1tY9PT2wsLC7u7vq6upVVVU9PT3Ly8t6enqTk5OBgYEnJyehoaFQUFBJSUlubm4iIiKHh4caGhpzc3NjY2PDw8NERETk5OQxMTHPYAHGAAAED0lEQVR4nO3a54KzKhAG4A87Vgy2RNT7v8tj2m6MxpIokj3v8393mADDiP77BwAAAAAAAAAAAAAAAAAAAAAAAAAAAPAex9l7BK80W4+sjDYO8I7g0GSUJxtHKWm1cYSlgqYquV3zYOtAaci3DrHIIclYaJmhhMV4EPph+ygzHdMspjUhOvVlhHMJkxFmBidjNLQIsQSTU3s58XIpgcYdyzZvj7RqGkkaECOEyok0oipoqGvnxEnISllRC4Nojaxgg/KMh7p5yZtobpRKC5y1q0yXFq0vZWFtXie83ebclzgNqd2GjOXF6zhGwrPueRPiRc1RYvRcnINKDPgrOR9kD8Kt+7dn9BxV9pIPAofpnbyJRWXO+AW7BJZ5uB+b6uRa3cQNnW3eufZk1+Iqq7AGTpLxpwlvd3l4khT/0aXOydrujX9iofWcuKG7UjrXZ4F7++G3j+RHzK2f8ybEDCV1rj3sXmC3DeNEnNpGP/G2c433aqWT+3jc7WL8tug9NivlF7i7n6Kz1aN7WrhhPTThbecqTvI61z5ONs39RPW6V9luLF7u+gCRGtvlnjLbM7QXiZO6qKT3MV0Px+yqD7BBJMyXaZ+3+Y67/IY9jG+9Ou9TcyTt88+878PyRfN42For/MO8SZ5b9Geap8bFGO2M6sN7ytzxY/Gqrt1JuXOdo+z2WB/MR1CVEZ2Y8HbK63061yHdaX+7qXXKmIfDB3h3yrk677vK54nKlv+PY1lwMdCi99RurM7d/2Nbc7P08qKKuatPT3jL5m/8rhtK7N4QFxzxx4wLe+Iou9FEIfseakrcH6VRzPvTlAnbG2tdHli0VGeb3zTuwEDN6eTzyK29WQv9zCtSFd71PPEHj2Jj/KBLuP66Q++zS5Xq26+BJX8RvlqhAesXiDGaq9xav2nCl4MWft550AiOuRO7s5f5NXFvhzvXuaqxXCy3KNO0aqVpxoavmsZY+h53rrOVM+bOWJrz9c9qoUznOqzX2KzEshXqXIcFr7f7J2q3ULOyP8rntWTLtJ3rztdQs+TrZy4Kxbf53WHlxC16Un2b/6hWzdxjvvrb/EeyYub2qVG3jxmwXu7unm9W3tKsk7im1jeo86xR5w1dkTvXhT4+3zVPSPv0b2WB+CjztnNV7WPzBYoPMvfEF3SuI6oF1y9dOlX6AXWGw9B13TQtZF/SuY7J3ph4g56+eJv/yul0rl3f1bmO8iffHXbYkfNVneu4eMGVlEi+4cl8ATZzy5vsr6z1B8V0d6cZ4bf2bxN6r6G7DNNW4wORTQRD37deWfpun7nK4rt2/82cqYe0+OOJXzgFFXZtGZfdbda2oDz2/9B5NuHgZ3HBWkVUJn+wqAMAAAAAAAAAAAAAAAAAAAAAAMD/2X8+lS8Dw21MzQAAAABJRU5ErkJggg==" alt="Logo" class="logo-mixed">
                  <div class="company-name-mixed">BİYO WEB POS</div>
                  <div class="step-title">MIXED ESC/POS + HTML TEST</div>
                </div>
                
                <div class="content-mixed">
                  <p><strong>Step 1: ESC/POS Text Formatting</strong></p>
                  <p>This section would normally contain ESC/POS formatted text with bold, double height, etc.</p>
                  <p>Since we're in HTML, this simulates the ESC/POS step.</p>
                </div>
                
                <div class="content-mixed">
                  <p><strong>Step 2: HTML with Embedded Logo</strong></p>
                  <p>This is HTML content with embedded logo.</p>
                  <p>The logo above was embedded as base64 image.</p>
                  <p>This step demonstrates HTML printing capabilities.</p>
                </div>
                
                <div class="content-mixed">
                  <p><strong>Step 3: ESC/POS QR Code</strong></p>
                  <p>This section would normally contain an ESC/POS generated QR code.</p>
                  <p>Since we're in HTML, we'll show an HTML QR code instead.</p>
                </div>
                
                <div class="qr-section">
                  <div class="step-title">STEP 4: HTML QR CODE</div>
                  <div class="qr-code">
                    <img src="${qrDataURL}" alt="QR Code" style="width: 100px; height: 100px; image-rendering: pixelated;" />
                  </div>
                  <div class="qr-text">
                    Scan QR code for mixed test results
                  </div>
                </div>
                
                <div class="summary">
                  <p><strong>Mixed Test Summary:</strong></p>
                  <p>✓ Step 1: ESC/POS text formatting (simulated)</p>
                  <p>✓ Step 2: HTML with embedded logo</p>
                  <p>✓ Step 3: ESC/POS QR code (simulated)</p>
                  <p>✓ Step 4: HTML QR code</p>
                  <p>All steps combined in single print job!</p>
                </div>
              </div>
            </body>
          </html>
        `;
        
        this.addTestResult(false, 'Sending combined mixed test to QZ Tray...');
        this.addTestResult(false, `HTML content length: ${combinedHTMLContent.length} characters`);
        this.addTestResult(false, `Target printer: ${this.terminalInfo.printer}`);
        
        const config = qz.configs.create(this.terminalInfo.printer);
        const data = [{
          type: 'html',
          format: 'plain',
          data: combinedHTMLContent
        }];
        
        await qz.print(config, data);
        this.addTestResult(true, 'Combined mixed test sent successfully');
        
        // Final Step: Paper Cut (separate command)
        this.addTestResult(false, 'Final step: Sending paper cut...');
        let cutCommands = '';
        cutCommands += '\x1B\x40'; // Initialize printer
        cutCommands += '\x1B\x61\x01'; // Center alignment
        cutCommands += '\n=== MIXED TEST COMPLETED ===\n';
        cutCommands += 'All steps completed successfully!\n';
        cutCommands += 'ESC/POS + HTML + QR Codes\n';
        cutCommands += '\x0A\x0A'; // Double line feed
        cutCommands += '\x1D\x56\x01'; // Full cut
        
        await this.sendRawCommands(cutCommands);
        this.addTestResult(true, 'Mixed test completed with paper cut');
        
      } catch (error) {
        console.error('Mixed test failed:', error);
        this.addTestResult(false, `Mixed test failed: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

  }
};
</script>

<style scoped>
.btn-group-vertical .btn {
  margin-bottom: 5px;
}
</style>
